// M4 — the ONE Business Web capture transport.
//
// Two surfaces reach the capture chain: the Scheduling appointment detail and the Unified Desktop
// active call. They must ask the SAME backend the SAME way, because these two calls are where the
// governed answers enter the browser:
//
//   readiness : the appointment detail projection's `capture_readiness`, computed server-side from
//               industry-profile applicability, commercial entitlement, role authorization, consent
//               policy and the patient's actual consent (api/scheduling_routes.py).
//   segment   : the M2 ingress (services/capture_transport.py), the single receiver that every
//               plane — Android, iOS, Web — posts to.
//
// A second copy of either would be a second place for the two surfaces to drift apart, and drift
// here means one surface offering a recording the other has already refused, for the same patient.
// There is no Web-specific readiness endpoint, no Web-specific eligibility, no Web-specific
// transcript path, and nothing in this file decides anything.
//
// The bearer token is INJECTED rather than imported. The canonical helper is `_getToken` in
// SchedulingManager — the one with the bounded refresh wait that closed the P0 hanging-action defect
// — and importing it here would close a cycle, because the Scheduling surface imports this module.
// Passing it in keeps one token authority without one.

import { tenantHeaders } from './tenantSession';

const captureBase = () => process.env.REACT_APP_BACKEND_URL || '';

/**
 * Bind the capture transport to an authenticated caller.
 *
 * @param {() => Promise<string>} getToken  the canonical bearer-token helper
 * @returns {{fetchReadiness: Function, sendSegment: Function}}
 */
export function createCaptureApi(getToken) {
  /**
   * The server's `capture_readiness` for one appointment, or null.
   *
   * Null on any failure. Unreadable readiness is NOT permission: `decide()` turns null into a
   * refusal, so a 403, a network drop and a stripped field all land on "not enabled" rather than on
   * whatever the page was holding before.
   */
  const fetchReadiness = async (appointmentId, businessId) => {
    if (!appointmentId || !businessId) return null;
    const token = await getToken();
    const url = `${captureBase()}/api/v1/scheduling/appointments/${encodeURIComponent(appointmentId)}`
      + `?business_id=${encodeURIComponent(businessId)}`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } });
    if (!r.ok) return null;
    const detail = await r.json();
    return (detail && detail.capture_readiness) || null;
  };

  /**
   * POST one captured segment to the M2 ingress.
   *
   * `headers` carries the governed segment metadata built by the producer (`buildHeaders`); the
   * body is raw LINEAR16. The tenant travels both as the query scope and in `tenantHeaders()`, the
   * same as every other Business-plane write.
   */
  const sendSegment = async (businessId, appointmentId, body, headers) => {
    const token = await getToken();
    const url = `${captureBase()}/api/v1/scheduling/appointments/`
      + `${encodeURIComponent(appointmentId)}/capture-segment`
      + `?business_id=${encodeURIComponent(businessId)}`;
    return fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, ...tenantHeaders(), ...headers },
      body,
    });
  };

  return { fetchReadiness, sendSegment };
}

export default createCaptureApi;
